import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

import { OrderFilesTab } from "../OrderFilesTab";
import { OrderProofsTab } from "../OrderProofsTab";
import { OrderConfiguratorTab } from "../OrderConfiguratorTab";
import { CommunicationHistory } from "../CommunicationHistory";

import { orderSchema, OrderFormData, Configuration } from "./types";
import { useOrderData } from "./hooks/useOrderData";
import { useOrderLines } from "./hooks/useOrderLines";
import { useInlineCreation } from "./hooks/useInlineCreation";
import { useLoadOrder } from "./hooks/useLoadOrder";
import { useOrderMutations, OrderMutationResult } from "./hooks/useOrderMutations";
import { usePriceCalculations, calculateCommission } from "./hooks/usePriceCalculations";
import { useOrderStatusMutation } from "@/hooks/useOrderStatusMutation";
import { BasicInfoTab } from "./tabs/BasicInfoTab";
import { OrderLinesTab } from "./tabs/OrderLinesTab";
import { CommissionTab } from "./tabs/CommissionTab";

interface OrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId?: string;
}

export function OrderDialog({ open, onOpenChange, orderId }: OrderDialogProps) {
  const [activeTab, setActiveTab] = useState("basic");
  const [configuration, setConfiguration] = useState<Configuration | null>(null);
  const [trackingLinkCopied, setTrackingLinkCopied] = useState(false);
  const isSubmittingRef = useRef(false);

  const form = useForm<OrderFormData>({
    resolver: zodResolver(orderSchema),
    defaultValues: { order_date: new Date(), order_type: "new_work", fulfillment_type: "installation", status: "draft" },
  });

  const { customers, partners, cemeteries, products } = useOrderData();
  const { orderLines, setOrderLines, configurationLineId, setConfigurationLineId, editingPrices, setEditingPrices, addOrderLine, updateOrderLine, removeOrderLine, selectProduct, getDisplayPrice, getAlternativePrice } = useOrderLines(configuration);
  
  const inlineCreation = useInlineCreation({
    onCustomerCreated: (id) => form.setValue("customer_id", id),
    onPartnerCreated: (id) => form.setValue("partner_id", id),
    onCemeteryCreated: (id) => form.setValue("cemetery_id", id),
  });
  
  const { isLoadingOrder, orderTrackingInfo, originalStatus, resetLoadOrderState } = useLoadOrder({ open, orderId, form, setOrderLines, setConfiguration, setConfigurationLineId });
  const { subtotal, vatAmount, totalAmount } = usePriceCalculations(orderLines);

  const partnerId = form.watch("partner_id");
  const commissionType = form.watch("commission_type");
  const commissionValue = form.watch("commission_value");
  const commissionAmount = calculateCommission(totalAmount, partnerId, commissionType, commissionValue);
  
  const statusNotificationMutation = useOrderStatusMutation();

  const handleSuccess = (result?: OrderMutationResult) => {
    // Lähetä sähköposti-ilmoitus jos status muuttui (keskitetty hook)
    if (result?.statusChanged && result.oldStatus && result.newStatus && result.id) {
      statusNotificationMutation.mutate({
        orderId: result.id,
        newStatus: result.newStatus,
        oldStatus: result.oldStatus,
        skipDbUpdate: true  // Status on jo tallennettu, vain notifikaatio
      }, {
        onError: (error) => {
          // Näytä varoitus, mutta älä estä dialogin sulkemista
          toast.error("Huomio", {
            description: "Tilaus tallennettiin, mutta sähköposti-ilmoituksen lähetys epäonnistui.",
          });
          console.error("[OrderDialog] Status notification failed:", error);
        }
      });
    }
    
    onOpenChange(false);
    form.reset();
    setOrderLines([]);
    setEditingPrices({});
    setActiveTab("basic");
    setConfiguration(null);
    setConfigurationLineId(null);
    resetLoadOrderState();
    inlineCreation.resetAll();
  };

  const { createOrderMutation, updateOrderMutation } = useOrderMutations({ orderId, orderLines, configuration, configurationLineId, originalStatus, onSuccess: handleSuccess });

  const onSubmit = (data: OrderFormData) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    const mutation = orderId ? updateOrderMutation : createOrderMutation;
    mutation.mutate(data, {
      onSettled: () => { isSubmittingRef.current = false; },
    });
  };

  // Show validation errors when form submit fails
  const handleFormSubmit = form.handleSubmit(onSubmit, (errors) => {
    const errorMessages: string[] = [];
    if (errors.customer_id) errorMessages.push("Asiakas on pakollinen");
    if (errors.order_date) errorMessages.push("Tilauspäivä on pakollinen");
    if (errors.order_type) errorMessages.push("Tilaustyyppi on pakollinen");
    if (errors.fulfillment_type) errorMessages.push("Toimitustapa on pakollinen");
    
    if (errorMessages.length > 0) {
      toast.error("Tarkista lomake", {
        description: errorMessages.join(", "),
      });
      setActiveTab("basic");
    }
  });

  /**
   * Käsittelee dialogin sulkemisen peruutuksen yhteydessä.
   * EI kutsu handleSuccess():ia, koska se voisi laukaista notifikaatioita.
   */
  const handleDialogCancel = () => {
    form.reset();
    setOrderLines([]);
    setEditingPrices({});
    setActiveTab("basic");
    setConfiguration(null);
    setConfigurationLineId(null);
    resetLoadOrderState();
    inlineCreation.resetAll();
    onOpenChange(false);
  };

  const handleDialogOpenChange = (openState: boolean) => {
    if (!openState) {
      // Kun dialogi suljetaan ulkopuolelta (esim. X-painike), käytä peruutuslogiikkaa
      handleDialogCancel();
      return;
    }
    onOpenChange(openState);
  };

  const copyTrackingLink = async () => {
    if (!orderTrackingInfo) return;
    const trackingUrl = `${window.location.origin}/track/${encodeURIComponent(orderTrackingInfo.order_number)}/${encodeURIComponent(orderTrackingInfo.tracking_token)}`;
    try {
      await navigator.clipboard.writeText(trackingUrl);
      setTrackingLinkCopied(true);
      toast.success("Seurantalinkki kopioitu leikepöydälle");
      setTimeout(() => setTrackingLinkCopied(false), 2000);
    } catch { toast.error("Virhe linkin kopioinnissa"); }
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        // Estetään dialogin sulkeutuminen kun klikataan Select-dropdownia
        // (joka renderöidään Portalin kautta dialogin ulkopuolelle).
        // Tämä myös estää vahinkosulkemiset taustaa klikkaamalla,
        // mikä on toivottavaa pitkässä lomakkeessa.
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex flex-row items-center justify-between gap-4">
          <DialogTitle>{orderId ? "Muokkaa tilausta" : "Luo uusi tilaus"}</DialogTitle>
          {orderId && orderTrackingInfo && (
            <Button type="button" variant="outline" size="sm" onClick={copyTrackingLink} className="flex items-center gap-2">
              {trackingLinkCopied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}Kopioi seurantalinkki
            </Button>
          )}
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleFormSubmit} className="space-y-6">
            {isLoadingOrder ? (
              <div className="flex items-center justify-center py-8"><p className="text-muted-foreground">Ladataan tilausta...</p></div>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-7">
                  <TabsTrigger value="basic">Perustiedot</TabsTrigger>
                  <TabsTrigger value="configurator">Konfiguraattori</TabsTrigger>
                  <TabsTrigger value="lines">Tilausrivit</TabsTrigger>
                  <TabsTrigger value="files">Tiedostot</TabsTrigger>
                  <TabsTrigger value="proofs">Vedokset</TabsTrigger>
                  <TabsTrigger value="communication" disabled={!orderId}>Viestintä</TabsTrigger>
                  <TabsTrigger value="commission" disabled={!partnerId}>Provisio</TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="mt-4">
                  <BasicInfoTab 
                    form={form} 
                    customers={customers} 
                    partners={partners} 
                    cemeteries={cemeteries} 
                    orderId={orderId} 
                    orderTrackingInfo={orderTrackingInfo}
                    // Customer inline creation
                    isCreatingCustomer={inlineCreation.showCustomerForm}
                    setIsCreatingCustomer={inlineCreation.setShowCustomerForm}
                    customerFormData={inlineCreation.customerForm}
                    setCustomerFormData={inlineCreation.setCustomerForm}
                    onCreateCustomer={inlineCreation.submitCustomer}
                    isCreatingCustomerPending={inlineCreation.customerPending}
                    // Partner inline creation
                    isCreatingPartner={inlineCreation.showPartnerForm}
                    setIsCreatingPartner={inlineCreation.setShowPartnerForm}
                    partnerFormData={inlineCreation.partnerForm}
                    setPartnerFormData={inlineCreation.setPartnerForm}
                    onCreatePartner={inlineCreation.submitPartner}
                    isCreatingPartnerPending={inlineCreation.partnerPending}
                    // Cemetery inline creation
                    isCreatingCemetery={inlineCreation.showCemeteryForm}
                    setIsCreatingCemetery={inlineCreation.setShowCemeteryForm}
                    cemeteryFormData={inlineCreation.cemeteryForm}
                    setCemeteryFormData={inlineCreation.setCemeteryForm}
                    onCreateCemetery={inlineCreation.submitCemetery}
                    isCreatingCemeteryPending={inlineCreation.cemeteryPending}
                  />
                </TabsContent>
                <TabsContent value="configurator" className="mt-4"><OrderConfiguratorTab orderId={orderId} configuration={configuration} onConfigurationChange={setConfiguration} selectedCustomerId={form.watch("customer_id")} /></TabsContent>
                <TabsContent value="lines" className="mt-4"><OrderLinesTab orderLines={orderLines} products={products} configuration={configuration} configurationLineId={configurationLineId} orderType={form.watch("order_type")} editingPrices={editingPrices} subtotal={subtotal} vatAmount={vatAmount} totalAmount={totalAmount} onAddLine={addOrderLine} onUpdateLine={updateOrderLine} onSelectProduct={(lineId, productId) => selectProduct(lineId, productId, products)} onRemoveLine={removeOrderLine} onEditingPriceChange={(lineId, value) => setEditingPrices(prev => value === undefined ? (({ [lineId]: _, ...rest }) => rest)(prev) : { ...prev, [lineId]: value })} getDisplayPrice={getDisplayPrice} getAlternativePrice={getAlternativePrice} /></TabsContent>
                <TabsContent value="files" className="mt-4"><OrderFilesTab orderId={orderId} /></TabsContent>
                <TabsContent value="proofs" className="mt-4"><OrderProofsTab orderId={orderId} orderNumber={orderId ? "Tilaus" : undefined} customerName={customers?.find(c => c.id === form.watch("customer_id"))?.first_name || customers?.find(c => c.id === form.watch("customer_id"))?.company_name || undefined} customerEmail={undefined} /></TabsContent>
                <TabsContent value="communication" className="mt-4">{orderId ? <CommunicationHistory orderId={orderId} maxHeight="350px" /> : <p className="text-sm text-muted-foreground text-center py-8">Tallenna tilaus ensin nähdäksesi viestintähistorian.</p>}</TabsContent>
                <TabsContent value="commission" className="mt-4"><CommissionTab form={form} commissionAmount={commissionAmount} /></TabsContent>
              </Tabs>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={handleDialogCancel}>Peruuta</Button>
              <Button type="submit" disabled={createOrderMutation.isPending || updateOrderMutation.isPending || isLoadingOrder}>
                {createOrderMutation.isPending || updateOrderMutation.isPending ? (orderId ? "Päivitetään..." : "Tallennetaan...") : (orderId ? "Päivitä tilaus" : "Tallenna tilaus")}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
